import { useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/PageHeader.jsx';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { zplTemplate, upsertZplTemplate } from '@/services/etiquetas.js';

const SERVICIOS = [
  { codigo: null, desc: 'Default' },
  { codigo: 1, desc: 'Estándar' },
  { codigo: 2, desc: 'Express' },
  { codigo: 3, desc: 'Prioritario' },
  { codigo: 4, desc: 'Internacional' },
  { codigo: 5, desc: 'Especial' },
];

const MOCK_DATA = {
  numero: 'ETQ-001',
  bultos: '3',
  localidad: 'Buenos Aires',
  tipo: 'Estándar',
  fecha: '10/01/2026',
  hora: '10:30',
  peso: '12.5',
  volumen: '0.25',
  remitente: 'Remitente Demo',
  destinatario: 'Destinatario Demo',
  remito: 'RM-1000',
  qr_text: 'ETQ-001',
  qr_url: 'https://demo.sinamonsoft.ar/q/etq/ETQ-001',
};

function renderPreview(template) {
  if (!template) return '';
  return Object.entries(MOCK_DATA).reduce(
    (acc, [key, value]) => acc.replaceAll(`{{${key}}}`, value),
    template
  );
}

export default function EtiquetasZpl() {
  const [servicio, setServicio] = useState(SERVICIOS[0]);
  const [template, setTemplate] = useState('');
  const [templateId, setTemplateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    zplTemplate(servicio.codigo)
      .then(data => {
        setTemplateId(data.id);
        setTemplate(data.zplTemplate || '');
      })
      .catch(err => setError(err.message || 'No se pudo cargar el template.'))
      .finally(() => setLoading(false));
  }, [servicio]);

  const previewContent = useMemo(() => renderPreview(template), [template]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await upsertZplTemplate({
        id: templateId,
        servicioCodigo: servicio.codigo,
        nombreTemplate: `Template ${servicio.desc}`,
        zplTemplate: template,
        activo: true,
      });
      setTemplateId(saved.id);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-6 pb-20">
      <PageHeader
        title="Templates ZPL"
        description="Configurá el template por servicio y probá el render con datos mock."
        breadcrumbs={[{ label: 'Administración', href: '/administracion' }, { label: 'Templates ZPL' }]}
      />

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6 shadow-[0_8px_40px_rgba(var(--shadow),0.12)]">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-[rgb(var(--fg))]">Servicio</label>
          <select
            value={servicio.codigo ?? ''}
            onChange={event => {
              const next = SERVICIOS.find(item => String(item.codigo ?? '') === event.target.value);
              if (next) setServicio(next);
            }}
            className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.5)] px-3 py-2 text-sm"
          >
            {SERVICIOS.map(item => (
              <option key={String(item.codigo)} value={String(item.codigo ?? '')}>
                {item.desc}
              </option>
            ))}
          </select>
          {loading && <span className="text-xs text-[rgba(var(--muted),0.9)]">Cargando...</span>}
        </div>

        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

        <textarea
          value={template}
          onChange={event => setTemplate(event.target.value)}
          rows={16}
          placeholder="Pegá tu ZPL con placeholders {{numero}}, {{remitente}}, etc."
          className="mt-4 w-full rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.6)] p-4 text-xs text-[rgb(var(--fg))]"
        />

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 text-sm"
            onClick={() => setPreviewOpen(true)}
          >
            Probar render
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? 'Guardando...' : 'Guardar template'}
          </button>
        </div>
      </section>

      <ModalBase isOpen={previewOpen} onClose={() => setPreviewOpen(false)} labelledBy="zpl-preview">
        <div className="space-y-4">
          <h2 id="zpl-preview" className="text-lg font-semibold text-[rgb(var(--fg))]">
            Preview ZPL
          </h2>
          <textarea
            readOnly
            value={previewContent}
            className="h-64 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.6)] p-3 text-xs"
          />
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 text-sm"
              onClick={() => setPreviewOpen(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      </ModalBase>
    </div>
  );
}
